import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './style.css';
import range from 'lodash/range';
import moment from 'moment';
import { getMonth, getYear } from "date-fns";
import "./style.css"
import MaskInput from '../MaskInput';
import { MASK } from '../MaskInput/utils';
import { buildAutomationChildTestId, buildAutomationValueTestId, resolveAutomationTestId } from '../InputField/testId';

const years = range(1910, parseInt(moment().format('YYYY')) + 20, 1);
const months = [
  "January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"
];

const CustomHeader = ({
  date, changeYear, changeMonth, decreaseMonth, increaseMonth,
  prevMonthButtonDisabled, nextMonthButtonDisabled, testId,
}) => (
  <div className='d-flex align-items-center justify-content-center gap-1 m-2' data-testid={testId}>
    <button
      type="button"
      style={{ padding: '2px 0' }}
      className='border-0 bg-light px-2 rounded-2'
      onClick={decreaseMonth}
      disabled={prevMonthButtonDisabled}
      data-testid={buildAutomationChildTestId(testId, "previous-month")}
    >
      {"<"}
    </button>
    <select
      className='border-0 bg-light py-1 px-2 rounded-2'
      value={getYear(date)}
      onChange={({ target: { value } }) => changeYear(value)}
      data-testid={buildAutomationChildTestId(testId, "year")}
    >
      {years.map((option) => (
        <option key={option} value={option} data-testid={buildAutomationValueTestId(buildAutomationChildTestId(testId, "year-option"), option)}>
          {option}
        </option>
      ))}
    </select>

    <select
      className='border-0 bg-light py-1 px-2 rounded-2'
      value={months[getMonth(date)]}
      onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
      data-testid={buildAutomationChildTestId(testId, "month")}
    >
      {months.map((option) => (
        <option key={option} value={option} data-testid={buildAutomationValueTestId(buildAutomationChildTestId(testId, "month-option"), option)}>
          {option}
        </option>
      ))}
    </select>

    <button
      type="button"
      style={{ padding: '2px 0' }}
      className='border-0 bg-light px-2 rounded-2'
      onClick={increaseMonth}
      disabled={nextMonthButtonDisabled}
      data-testid={buildAutomationChildTestId(testId, "next-month")}
    >
      {">"}
    </button>
  </div>
);

const yyyymmddToDateFormat = (yyyymmdd) => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return null;
  const year = yyyymmdd.slice(0, 4);
  const month = parseInt(yyyymmdd.slice(4, 6), 10) - 1; // month is 0-indexed in JavaScript Date
  const day = yyyymmdd.slice(6, 8);
  return new Date(year, month, day);
};

// Parse whatever date representation arrives in `value` into a Date for the picker to display.
// onChange writes YYYYMMDD (via dateFormatToYYYYMMDD), so that is the primary contract and is kept
// byte-for-byte. But a stored/extraction-prefilled value can also be an ISO date ("1990-05-14") or
// other common string; those are 10 chars, so the old 8-digit-only parser returned null and the
// field rendered BLANK even though it held a valid value (no error shown, nothing to confirm — a
// silent data-loss trap in the doc-first "confirm each pre-filled value" flow). Fall back to moment
// for anything that is not 8 digits. Only widens what parses — a valid YYYYMMDD still takes the
// original path, so every existing caller is unchanged.
const parseIncomingDateValue = (raw) => {
  if (raw === undefined || raw === null) return null;
  const str = String(raw).trim();
  if (!str || str === 'null' || str === 'undefined') return null;

  if (/^\d{8}$/.test(str)) {
    const fromYyyymmdd = yyyymmddToDateFormat(str);
    if (fromYyyymmdd && !isNaN(fromYyyymmdd)) return fromYyyymmdd;
  }

  const parsed = moment(str, [
    moment.ISO_8601,
    'YYYY-MM-DD',
    'YYYY/MM/DD',
    'DD-MM-YYYY',
    'DD/MM/YYYY',
    'MM/DD/YYYY',
  ], true);
  if (parsed.isValid()) {
    const d = parsed.toDate();
    // Rebuild at local midnight so the displayed calendar day matches the stored one regardless of TZ.
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  return null;
};

export const dateFormatToYYYYMMDD = (date) => {
  if (!date) return '';
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return year + month + day;
};

const isValidDate = (dateString) => {
  // Check if the date is valid using moment.js
  const date = moment(dateString, 'DDMMYYYY', true);
  if (!date.isValid()) return false;

  // Check if the year is greater than 1910
  const year = date.year();
  return year > 1910;
};


export default function DateSelectCustom(props) {
  const { id, value, onChange, isDisabled, placeholder, outline, label, error, showErr, className, required, minDate, maxDate, format = "dd-mm-yyyy", displayFormat = "dd/MM/yyyy" } = props;
  const { prevValue, ...restProps } = props;
  const fieldTestId = resolveAutomationTestId(props["data-testid"], id, props.name);

  const [startDate, setStartDate] = useState(() => parseIncomingDateValue(value));
  const [inputError, setInputError] = useState('');

  useEffect(() => {
    setStartDate(parseIncomingDateValue(value));
  }, [value]);

  const handleDateChange = (date) => {
    if (date && isValidDate(moment(date).format('DDMMYYYY'))) {
      setStartDate(date);
      const formattedDate = dateFormatToYYYYMMDD(date);
      onChange(formattedDate);
      setInputError('');
    } else {
      setInputError('Invalid date');
    }
  };

  const handleBlur = () => {
    if (!startDate) {
      setInputError(error || 'Invalid date');
    } else if (!isValidDate(moment(startDate).format('DDMMYYYY'))) {
      setInputError('Invalid date');
    }
  };

  const calendarPopoverTestId = buildAutomationChildTestId(fieldTestId, "calendar-popover");
  const calendarDayTestId = buildAutomationChildTestId(fieldTestId, "calendar-day");

  return (
    <div className={`d-flex flex-1 w-100 flex-column ${className}`} data-testid={buildAutomationChildTestId(fieldTestId, "date-picker")}>

      <div className={`d-flex flex-1 w-100 align-items-center`} data-testid={buildAutomationChildTestId(fieldTestId, "date-picker-control")}>
        <DatePicker
          id={id}
          name={props.name}
          selected={startDate}
          onChange={handleDateChange}
          onBlur={handleBlur}
          placeholderText={placeholder}
          dateFormat={displayFormat}
          required={props?.required}
          disabled={isDisabled}
          className={`my-form-input flex-1 w-100`}
          wrapperClassName="date-picker react-datepicker-wrapper flex-1 w-100 mb-1"
          renderCustomHeader={(headerProps) => (
            <CustomHeader
              {...headerProps}
              testId={buildAutomationChildTestId(fieldTestId, "calendar-header")}
            />
          )}
          calendarContainer={({ className: calendarClassName, children }) => (
            <div className={calendarClassName} data-testid={calendarPopoverTestId}>
              {children}
            </div>
          )}
          renderDayContents={(day, date) => (
            <span data-testid={buildAutomationValueTestId(calendarDayTestId, moment(date).format("YYYY-MM-DD"))}>
              {day}
            </span>
          )}
          minDate={minDate || ""} // Prevent selecting past dates
          maxDate={maxDate || ""} // Prevent selecting past dates
          customInput={
            <MaskInput id={id} data-testid={fieldTestId} error={inputError ? inputError : error} {...MASK[format]} datePicker={true} {...restProps} prevValue={{ has: prevValue?.has, value: ["", null]?.includes(prevValue?.value) ? "" : moment(prevValue?.value , "YYYYMMDD").format(MASK[format].format || "DD/MM/YYYY") }} />
          }
        />
      </div>

    </div>
  );
}
